import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  MapPin, Calendar, Clock, Plus, ChevronLeft, ChevronRight, X, Edit2, Trash2, 
  Plane, Hotel, Car, Coffee, Users, Camera, FileText, Upload, Download,
  DollarSign, CheckCircle2, AlertCircle, Loader2, MoreVertical, ChevronDown,
  Briefcase, Film, Globe, Sun, Cloud, CloudRain, Eye, Image, File, Link2, ExternalLink,
  CheckSquare, Square
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';
import ItineraryMap from '../components/ItineraryMap';

// Use window.location.origin to ensure requests go to the same domain
// This bypasses any proxy rewriting that might be happening with env vars
const API = window.location.origin;

// API helper using fetch (matches working pages like KnowledgeBase)
const apiCall = async (method, url, body = null) => {
  const options = {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
  };
  if (body) options.body = JSON.stringify(body);
  
  const response = await fetch(url, options);
  const text = await response.text();
  
  if (!text || text.trim() === '') {
    if (response.ok) return {};
    throw new Error(`HTTP ${response.status}`);
  }
  
  try {
    const data = JSON.parse(text);
    if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`);
    return data;
  } catch (e) {
    if (e.message.includes('HTTP')) throw e;
    console.error('API parse error:', e, 'Response:', text.substring(0, 100));
    throw new Error('Failed to parse server response');
  }
};

// FormData upload helper
const apiUpload = async (url, formData) => {
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
  });
  const text = await response.text();
  
  if (!text || text.trim() === '') {
    if (response.ok) return {};
    throw new Error(`HTTP ${response.status}`);
  }
  
  const data = JSON.parse(text);
  if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`);
  return data;
};

// Event type icons and colors
const EVENT_TYPES = {
  flight: { icon: Plane, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Flight' },
  hotel: { icon: Hotel, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Hotel' },
  transport: { icon: Car, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Transport' },
  meal: { icon: Coffee, color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'Meal' },
  meeting: { icon: Users, color: 'text-cyan-400', bg: 'bg-cyan-500/20', label: 'Meeting' },
  shoot: { icon: Camera, color: 'text-pink-400', bg: 'bg-pink-500/20', label: 'Shoot' },
  activity: { icon: Globe, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Activity' },
  conference: { icon: Briefcase, color: 'text-indigo-400', bg: 'bg-indigo-500/20', label: 'Conference' },
  production: { icon: Film, color: 'text-rose-400', bg: 'bg-rose-500/20', label: 'Production' },
  other: { icon: Plus, color: 'text-gray-400', bg: 'bg-gray-500/20', label: 'Other' },
};

const ITINERARY_TYPES = [
  { value: 'travel', label: 'Travel', icon: Plane },
  { value: 'business', label: 'Business', icon: Briefcase },
  { value: 'production', label: 'Production', icon: Film },
  { value: 'custom', label: 'Custom', icon: Globe },
];

// Map event types to budget categories
const EVENT_TYPE_TO_BUDGET_CATEGORY = {
  flight: 'Transportation',
  transport: 'Transportation',
  hotel: 'Accommodation',
  meal: 'Food & Dining',
  activity: 'Activities',
  meeting: 'Activities',
  shoot: 'Activities',
  conference: 'Activities',
  production: 'Activities',
};

const Itineraries = () => {
  const { user } = useAuth();
  const userId = user?.user_id || user?.id || user?._id;

  const [loading, setLoading] = useState(true);
  const [itineraries, setItineraries] = useState([]);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [view, setView] = useState('list'); // 'list' or 'detail'
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // Form states
  const [newItinerary, setNewItinerary] = useState({
    title: '',
    description: '',
    itinerary_type: 'travel',
    start_date: '',
    end_date: '',
    location: ''
  });
  
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    event_type: 'activity',
    custom_type: '',
    notes: '',
    cost: '',
    link: ''
  });

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Attachment preview
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Event image states
  const [eventImagePreview, setEventImagePreview] = useState(null);
  const [loadingEventImage, setLoadingEventImage] = useState(false);
  const [uploadingEventImage, setUploadingEventImage] = useState(false);
  const [eventImageCache, setEventImageCache] = useState({}); // Cache for thumbnail previews

  // Load itineraries
  const loadItineraries = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await apiCall('GET', `${API}/api/itineraries?user_id=${userId}`);
      setItineraries(data.items || []);
      
      // Also load dashboard data
      const dashboard = await apiCall('GET', `${API}/api/itineraries/dashboard/upcoming?user_id=${userId}`);
      setDashboardData(dashboard);
    } catch (e) {
      console.error('Failed to load itineraries:', e);
      toast.error('Failed to load itineraries');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadItineraries();
  }, [loadItineraries]);

  // Load event image thumbnails when itinerary is selected
  useEffect(() => {
    const loadEventImages = async () => {
      if (!selectedItinerary?.events || !userId) return;
      
      // Find events with images that aren't cached yet
      const eventsWithImages = selectedItinerary.events.filter(e => e.image && !eventImageCache[e.id]);
      
      // Load each image
      for (const event of eventsWithImages) {
        try {
          const data = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}/events/${event.id}/image?user_id=${userId}`);
          if (data.data_url) {
            setEventImageCache(prev => ({ ...prev, [event.id]: data.data_url }));
          }
        } catch (e) {
          console.log(`Failed to load thumbnail for event ${event.id}:`, e);
        }
      }
    };
    
    loadEventImages();
  }, [selectedItinerary?.id, selectedItinerary?.events, userId]);

  // Create itinerary
  const handleCreateItinerary = async () => {
    if (!newItinerary.title || !newItinerary.start_date || !newItinerary.end_date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const created = await apiCall('POST', `${API}/api/itineraries?user_id=${userId}`, newItinerary);
      toast.success('Itinerary created');
      setShowCreateModal(false);
      setNewItinerary({ title: '', description: '', itinerary_type: 'travel', start_date: '', end_date: '', location: '' });
      await loadItineraries();
      setSelectedItinerary(created);
      setView('detail');
    } catch (e) {
      toast.error(e.message || 'Failed to create itinerary');
    }
  };

  // Delete itinerary
  const handleDeleteItinerary = async (id) => {
    if (!window.confirm('Delete this itinerary? This cannot be undone.')) return;
    try {
      await apiCall('DELETE', `${API}/api/itineraries/${id}?user_id=${userId}`);
      toast.success('Itinerary deleted');
      if (selectedItinerary?.id === id) {
        setSelectedItinerary(null);
        setView('list');
      }
      await loadItineraries();
    } catch (e) {
      toast.error(e.message || 'Failed to delete itinerary');
    }
  };

  // Add/Update event
  const handleSaveEvent = async () => {
    if (!newEvent.title || !newEvent.date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      if (editingEvent) {
        await apiCall('PATCH', `${API}/api/itineraries/${selectedItinerary.id}/events/${editingEvent.id}?user_id=${userId}`, newEvent);
        toast.success('Event updated');
      } else {
        await apiCall('POST', `${API}/api/itineraries/${selectedItinerary.id}/events?user_id=${userId}`, newEvent);
        toast.success('Event added');
      }
      
      setShowEventModal(false);
      setEditingEvent(null);
      setNewEvent({ title: '', date: '', start_time: '', end_time: '', location: '', description: '', event_type: 'activity', custom_type: '', notes: '', cost: '', link: '' });
      
      // Reload itinerary
      const updated = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}?user_id=${userId}`);
      setSelectedItinerary(updated);
      await loadItineraries();
    } catch (e) {
      toast.error(e.message || 'Failed to save event');
    }
  };

  // Delete event
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await apiCall('DELETE', `${API}/api/itineraries/${selectedItinerary.id}/events/${eventId}?user_id=${userId}`);
      toast.success('Event deleted');
      const updated = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}?user_id=${userId}`);
      setSelectedItinerary(updated);
      await loadItineraries();
    } catch (e) {
      toast.error(e.message || 'Failed to delete event');
    }
  };

  // Toggle event completion
  const handleToggleEventComplete = async (eventId) => {
    try {
      const result = await apiCall('PATCH', `${API}/api/itineraries/${selectedItinerary.id}/events/${eventId}/toggle-complete?user_id=${userId}`);
      // Update local state immediately for responsive UI
      setSelectedItinerary(prev => ({
        ...prev,
        events: prev.events.map(e => 
          e.id === eventId ? { ...e, completed: result.completed } : e
        )
      }));
    } catch (e) {
      toast.error(e.message || 'Failed to update event');
    }
  };

  // Update budget
  const handleUpdateBudget = async (category, field, value) => {
    try {
      await apiCall('PATCH', `${API}/api/itineraries/${selectedItinerary.id}/budget?user_id=${userId}`, {
        category,
        [field]: parseFloat(value) || 0
      });
      const updated = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}?user_id=${userId}`);
      setSelectedItinerary(updated);
    } catch (e) {
      toast.error(e.message || 'Failed to update budget');
    }
  };

  // Upload attachment
  const handleUploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('file', file);
    
    try {
      await apiUpload(`${API}/api/itineraries/${selectedItinerary.id}/attachments`, formData);
      toast.success('File uploaded');
      const updated = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}?user_id=${userId}`);
      setSelectedItinerary(updated);
    } catch (e) {
      toast.error(e.message || 'Failed to upload file');
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await apiCall('DELETE', `${API}/api/itineraries/${selectedItinerary.id}/attachments/${attachmentId}?user_id=${userId}`);
      toast.success('Attachment deleted');
      const updated = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}?user_id=${userId}`);
      setSelectedItinerary(updated);
    } catch (e) {
      toast.error(e.message || 'Failed to delete attachment');
    }
  };

  // View/Download attachment
  const handleViewAttachment = async (attachment) => {
    setLoadingPreview(true);
    try {
      const data = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}/attachments/${attachment.id}?user_id=${userId}`);
      setPreviewAttachment({
        ...attachment,
        data_url: data.data_url
      });
    } catch (e) {
      toast.error(e.message || 'Failed to load attachment');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Download attachment
  const handleDownloadAttachment = () => {
    if (!previewAttachment?.data_url) return;
    const link = document.createElement('a');
    link.href = previewAttachment.data_url;
    link.download = previewAttachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Upload event image
  const handleUploadEventImage = async (eventId, file) => {
    if (!file) return;
    
    // Validate file type
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      toast.error('Please upload an image file (jpg, png, gif, webp)');
      return;
    }
    
    setUploadingEventImage(true);
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('file', file);
    
    try {
      await apiUpload(`${API}/api/itineraries/${selectedItinerary.id}/events/${eventId}/image`, formData);
      toast.success('Image uploaded');
      
      // Reload itinerary to get updated event
      const updated = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}?user_id=${userId}`);
      setSelectedItinerary(updated);
      
      // Also fetch and cache the image immediately for thumbnail display
      try {
        const imgData = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}/events/${eventId}/image?user_id=${userId}`);
        if (imgData.data_url) {
          setEventImageCache(prev => ({ ...prev, [eventId]: imgData.data_url }));
        }
      } catch (e) {
        console.log('Could not cache thumbnail:', e);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to upload image');
    } finally {
      setUploadingEventImage(false);
    }
  };

  // View event image in popup
  const handleViewEventImage = async (eventId) => {
    setLoadingEventImage(true);
    try {
      const data = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}/events/${eventId}/image?user_id=${userId}`);
      setEventImagePreview(data);
    } catch (e) {
      toast.error(e.message || 'Failed to load image');
    } finally {
      setLoadingEventImage(false);
    }
  };

  // Delete event image
  const handleDeleteEventImage = async (eventId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await apiCall('DELETE', `${API}/api/itineraries/${selectedItinerary.id}/events/${eventId}/image?user_id=${userId}`);
      toast.success('Image deleted');
      
      // Clear from cache
      setEventImageCache(prev => {
        const newCache = { ...prev };
        delete newCache[eventId];
        return newCache;
      });
      
      const updated = await apiCall('GET', `${API}/api/itineraries/${selectedItinerary.id}?user_id=${userId}`);
      setSelectedItinerary(updated);
    } catch (e) {
      toast.error(e.message || 'Failed to delete image');
    }
  };

  // Group events by date
  const eventsByDate = useMemo(() => {
    if (!selectedItinerary?.events) return {};
    const grouped = {};
    for (const event of selectedItinerary.events) {
      const date = event.date;
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(event);
    }
    // Sort events within each date by start_time
    for (const date in grouped) {
      grouped[date].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
    }
    return grouped;
  }, [selectedItinerary]);

  // Calculate budget totals including event costs
  const budgetWithEventCosts = useMemo(() => {
    if (!selectedItinerary?.budget?.categories) return [];
    
    // Start with base budget categories
    const categoryMap = {};
    for (const cat of selectedItinerary.budget.categories) {
      categoryMap[cat.name] = {
        name: cat.name,
        budgeted: cat.budgeted || 0,
        actual: cat.actual || 0,
        eventCosts: 0
      };
    }
    
    // Add event costs to appropriate categories
    for (const event of (selectedItinerary.events || [])) {
      const cost = parseFloat(event.cost) || 0;
      if (cost > 0) {
        const budgetCategory = EVENT_TYPE_TO_BUDGET_CATEGORY[event.event_type] || 'Miscellaneous';
        if (categoryMap[budgetCategory]) {
          categoryMap[budgetCategory].eventCosts += cost;
        } else if (categoryMap['Miscellaneous']) {
          categoryMap['Miscellaneous'].eventCosts += cost;
        }
      }
    }
    
    // Convert back to array and calculate total actual (base + events)
    return Object.values(categoryMap).map(cat => ({
      ...cat,
      totalActual: cat.actual + cat.eventCosts
    }));
  }, [selectedItinerary]);

  // Calculate budget totals
  const budgetTotals = useMemo(() => {
    if (!budgetWithEventCosts.length) return { budgeted: 0, actual: 0 };
    return budgetWithEventCosts.reduce(
      (acc, cat) => ({
        budgeted: acc.budgeted + (cat.budgeted || 0),
        actual: acc.actual + (cat.totalActual || 0)
      }),
      { budgeted: 0, actual: 0 }
    );
  }, [budgetWithEventCosts]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Calculate days until
  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr + 'T00:00:00');
    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff < 0) return `${Math.abs(diff)} days ago`;
    return `${diff} days away`;
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'active': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'completed': return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-300 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-white/50" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6 md:p-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              {view === 'detail' && selectedItinerary ? (
                <button 
                  onClick={() => { setView('list'); setSelectedItinerary(null); }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <ChevronLeft size={26} />
                </button>
              ) : (
                <MapPin size={26} className="text-white" />
              )}
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                {view === 'detail' && selectedItinerary ? selectedItinerary.title : 'Itineraries'}
              </h1>
            </div>
            {view === 'detail' && selectedItinerary && (
              <p className="text-gray-400 text-sm ml-9">
                {formatDate(selectedItinerary.start_date)} - {formatDate(selectedItinerary.end_date)}
                {selectedItinerary.location && ` · ${selectedItinerary.location}`}
              </p>
            )}
          </div>
          
          <div className="flex gap-3">
            {view === 'list' && (
              <button
                onClick={() => setShowCreateModal(true)}
                data-testid="create-itinerary-btn"
                className="inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                <Plus size={18} /> New Itinerary
              </button>
            )}
            {view === 'detail' && selectedItinerary && (
              <button
                onClick={() => {
                  setNewEvent({ ...newEvent, date: selectedItinerary.start_date });
                  setShowEventModal(true);
                }}
                data-testid="add-event-btn"
                className="inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                <Plus size={18} /> Add Event
              </button>
            )}
          </div>
        </div>

        {/* List View */}
        {view === 'list' && (
          <>
            {/* Dashboard - Upcoming Overview */}
            {dashboardData?.next_event && (
              <div className="mb-8 bg-black border border-white/30 rounded-2xl p-6 transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Clock size={18} /> Next Up
                </h2>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <p className="text-2xl font-bold">{dashboardData.next_event.title}</p>
                    <p className="text-gray-400 text-sm mt-1">
                      {formatDate(dashboardData.next_event.date)} 
                      {dashboardData.next_event.start_time && ` at ${formatTime(dashboardData.next_event.start_time)}`}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      Part of: {dashboardData.next_event.itinerary_title}
                    </p>
                  </div>
                  <div className="flex items-center gap-6">
                    {dashboardData.next_event.location && (
                      <div className="text-center">
                        <MapPin size={20} className="mx-auto text-gray-400 mb-1" />
                        <p className="text-sm text-gray-300">{dashboardData.next_event.location}</p>
                      </div>
                    )}
                    <div className="text-center">
                      <Calendar size={20} className="mx-auto text-gray-400 mb-1" />
                      <p className="text-sm text-gray-300">{getDaysUntil(dashboardData.next_event.date)}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Itineraries Grid */}
            {itineraries.length === 0 ? (
              <div className="bg-black border border-white/30 rounded-2xl p-12 text-center transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
                <MapPin size={48} className="mx-auto text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Itineraries Yet</h3>
                <p className="text-gray-400 mb-6">Create your first itinerary to start planning trips, meetings, or productions.</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  <Plus size={18} /> Create Itinerary
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {itineraries.map((itin) => {
                  const TypeIcon = ITINERARY_TYPES.find(t => t.value === itin.itinerary_type)?.icon || Globe;
                  return (
                    <div
                      key={itin.id}
                      className="relative bg-black border border-white/30 rounded-xl p-5 transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)] cursor-pointer group"
                      onClick={() => { setSelectedItinerary(itin); setView('detail'); }}
                      data-testid={`itinerary-card-${itin.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                            <TypeIcon size={20} className="text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-base group-hover:text-white transition-colors">{itin.title}</h3>
                            <p className="text-xs text-gray-500">{formatDate(itin.start_date)}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${getStatusColor(itin.status)}`}>
                          {itin.status}
                        </span>
                      </div>
                      
                      {itin.location && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-2">
                          <MapPin size={12} /> {itin.location}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500 mt-3 pt-3 border-t border-white/10">
                        <span>{itin.events?.length || 0} events</span>
                        <span>{getDaysUntil(itin.start_date)}</span>
                      </div>
                      
                      {/* Quick delete button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteItinerary(itin.id); }}
                        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-400 hover:bg-red-500/20 transition-all p-1.5 rounded-lg bg-black/50"
                        title="Delete itinerary"
                        data-testid={`delete-itinerary-${itin.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Detail View */}
        {view === 'detail' && selectedItinerary && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - Timeline */}
            <div className="lg:col-span-2 space-y-6">
              {/* Timeline */}
              <div className="bg-black border border-white/30 rounded-2xl p-6 transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar size={18} /> Schedule
                </h2>
                
                {Object.keys(eventsByDate).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No events scheduled yet</p>
                    <button
                      onClick={() => {
                        setNewEvent({ ...newEvent, date: selectedItinerary.start_date });
                        setShowEventModal(true);
                      }}
                      className="mt-3 text-sm text-white underline hover:no-underline"
                    >
                      Add your first event
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(eventsByDate)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([date, events]) => (
                        <div key={date}>
                          <h3 className="text-sm font-medium text-gray-400 mb-3 sticky top-0 bg-black py-1">
                            {formatDate(date)}
                          </h3>
                          <div className="space-y-2 ml-4 border-l border-white/10 pl-4">
                            {events.map((event) => {
                              const eventConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
                              const EventIcon = eventConfig.icon;
                              const displayLabel = event.event_type === 'other' && event.custom_type 
                                ? event.custom_type 
                                : eventConfig.label;
                              return (
                                <div
                                  key={event.id}
                                  className={`relative group bg-[#0C0E13] border rounded-lg p-4 hover:border-white/30 transition-all ${
                                    event.completed ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/10'
                                  }`}
                                >
                                  {/* Timeline dot */}
                                  <div className={`absolute -left-[1.35rem] top-5 w-2.5 h-2.5 rounded-full ${event.completed ? 'bg-emerald-500' : eventConfig.bg} border-2 border-black`} />
                                  
                                  <div className="flex items-start gap-3">
                                    {/* Checkbox */}
                                    <button
                                      onClick={() => handleToggleEventComplete(event.id)}
                                      className={`flex-shrink-0 mt-0.5 transition-colors ${
                                        event.completed ? 'text-emerald-400' : 'text-gray-500 hover:text-white'
                                      }`}
                                      title={event.completed ? 'Mark incomplete' : 'Mark complete'}
                                      data-testid={`event-checkbox-${event.id}`}
                                    >
                                      {event.completed ? <CheckSquare size={18} /> : <Square size={18} />}
                                    </button>
                                    
                                    <div className={`w-9 h-9 rounded-lg ${eventConfig.bg} flex items-center justify-center flex-shrink-0 ${event.completed ? 'opacity-50' : ''}`} title={displayLabel}>
                                      <EventIcon size={16} className={eventConfig.color} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between gap-2">
                                        <div>
                                          <p className={`font-medium text-sm ${event.completed ? 'line-through text-gray-500' : ''}`}>{event.title}</p>
                                          <div className="flex items-center gap-2 mt-0.5">
                                            {event.start_time && (
                                              <p className={`text-xs ${event.completed ? 'text-gray-600' : 'text-gray-500'}`}>
                                                {formatTime(event.start_time)}
                                                {event.end_time && ` - ${formatTime(event.end_time)}`}
                                              </p>
                                            )}
                                            {event.cost > 0 && (
                                              <span className={`text-xs font-medium ${event.completed ? 'text-gray-500' : 'text-emerald-400'}`}>
                                                ${parseFloat(event.cost).toFixed(0)}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                          {/* Image upload button */}
                                          <label className="p-1 text-gray-500 hover:text-blue-400 cursor-pointer" title="Upload image">
                                            <Image size={12} />
                                            <input
                                              type="file"
                                              accept="image/*"
                                              className="hidden"
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) handleUploadEventImage(event.id, file);
                                                e.target.value = '';
                                              }}
                                            />
                                          </label>
                                          <button
                                            onClick={() => {
                                              setEditingEvent(event);
                                              setNewEvent({
                                                title: event.title,
                                                date: event.date,
                                                start_time: event.start_time || '',
                                                end_time: event.end_time || '',
                                                location: event.location || '',
                                                description: event.description || '',
                                                event_type: event.event_type,
                                                custom_type: event.custom_type || '',
                                                notes: event.notes || '',
                                                cost: event.cost || '',
                                                link: event.link || ''
                                              });
                                              setShowEventModal(true);
                                            }}
                                            className="p-1 text-gray-500 hover:text-white"
                                          >
                                            <Edit2 size={12} />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteEvent(event.id)}
                                            className="p-1 text-gray-500 hover:text-red-400"
                                          >
                                            <Trash2 size={12} />
                                          </button>
                                        </div>
                                      </div>
                                      {event.location && (
                                        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                          <MapPin size={10} /> {event.location}
                                        </p>
                                      )}
                                      {event.link && (
                                        <a 
                                          href={event.link} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-xs text-blue-400 hover:text-blue-300 mt-1 flex items-center gap-1"
                                        >
                                          <ExternalLink size={10} /> View Link
                                        </a>
                                      )}
                                      {event.description && (
                                        <p className="text-xs text-gray-500 mt-2">{event.description}</p>
                                      )}
                                      {event.notes && (
                                        <p className="text-xs text-gray-600 mt-1 italic">Note: {event.notes}</p>
                                      )}
                                      
                                      {/* Event Image Thumbnail */}
                                      {event.image && (
                                        <div className="mt-3 relative group/img">
                                          <button
                                            onClick={() => handleViewEventImage(event.id)}
                                            className="block w-full max-w-[200px] aspect-video bg-black/50 rounded-lg overflow-hidden border border-white/10 hover:border-white/30 transition-all relative"
                                            data-testid={`event-image-${event.id}`}
                                          >
                                            {/* Show actual image thumbnail if we have cached data, otherwise show placeholder */}
                                            {eventImageCache[event.id] ? (
                                              <img 
                                                src={eventImageCache[event.id]} 
                                                alt={event.image.filename}
                                                className="w-full h-full object-cover"
                                              />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                <div className="text-center">
                                                  <Image size={20} className="mx-auto mb-1" />
                                                  <span className="text-[10px]">{event.image.filename}</span>
                                                </div>
                                              </div>
                                            )}
                                            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity">
                                              <Eye size={16} className="text-white" />
                                            </div>
                                          </button>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleDeleteEventImage(event.id); }}
                                            className="absolute top-1 right-1 p-1 bg-black/70 rounded text-gray-400 hover:text-red-400 opacity-0 group-hover/img:opacity-100 transition-opacity"
                                            title="Delete image"
                                          >
                                            <Trash2 size={10} />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              
              {/* Map View */}
              <ItineraryMap 
                events={selectedItinerary.events || []} 
                itineraryLocation={selectedItinerary.location}
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Budget Tracker */}
              <div className="bg-black border border-white/30 rounded-2xl p-5 transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
                <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
                  <DollarSign size={16} /> Budget
                </h2>
                
                <div className="space-y-3">
                  {budgetWithEventCosts.map((cat) => (
                    <div key={cat.name} className="text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-400">{cat.name}</span>
                        <span className={cat.totalActual > cat.budgeted ? 'text-red-400' : 'text-green-400'}>
                          ${cat.totalActual?.toFixed(0) || 0} / ${cat.budgeted?.toFixed(0) || 0}
                        </span>
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${cat.totalActual > cat.budgeted ? 'bg-red-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min((cat.totalActual / (cat.budgeted || 1)) * 100, 100)}%` }}
                        />
                      </div>
                      {cat.eventCosts > 0 && (
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          Includes ${cat.eventCosts.toFixed(0)} from events
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-300 font-medium">Total</span>
                    <span className={budgetTotals.actual > budgetTotals.budgeted ? 'text-red-400' : 'text-green-400'}>
                      ${budgetTotals.actual.toFixed(0)} / ${budgetTotals.budgeted.toFixed(0)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Remaining: ${(budgetTotals.budgeted - budgetTotals.actual).toFixed(0)}
                  </p>
                </div>
              </div>

              {/* Attachments */}
              <div className="bg-black border border-white/30 rounded-2xl p-5 transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
                <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
                  <FileText size={16} /> Attachments
                </h2>
                
                <input
                  type="file"
                  id="attachment-upload"
                  className="hidden"
                  onChange={handleUploadAttachment}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.mp4,.mov,.mp3,.wav,.doc,.docx,.txt"
                />
                
                <label
                  htmlFor="attachment-upload"
                  className="flex items-center justify-center gap-2 w-full py-2.5 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:border-white/40 hover:text-white cursor-pointer transition-all"
                >
                  <Upload size={14} /> Upload File
                </label>
                
                {selectedItinerary.attachments?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {selectedItinerary.attachments.map((att) => {
                      // Choose icon based on file type
                      const IconComponent = att.file_type === 'image' ? Image : 
                                           att.file_type === 'pdf' ? FileText : File;
                      return (
                        <div 
                          key={att.id}
                          className="flex items-center gap-2 p-2.5 bg-[#0C0E13] rounded-lg group hover:bg-[#1a1d24] cursor-pointer transition-all"
                          onClick={() => handleViewAttachment(att)}
                          data-testid={`attachment-${att.id}`}
                        >
                          <IconComponent size={14} className="text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-300 truncate flex-1">{att.name}</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleViewAttachment(att); }}
                              className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-blue-400 transition-all p-1"
                              title="View"
                            >
                              <Eye size={12} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteAttachment(att.id); }}
                              className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-all p-1"
                              title="Delete"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Quick Info */}
              <div className="bg-black border border-white/30 rounded-2xl p-5 transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
                <h2 className="text-base font-semibold mb-4">Details</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Type</span>
                    <span className="text-gray-300 capitalize">{selectedItinerary.itinerary_type}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(selectedItinerary.status)}`}>
                      {selectedItinerary.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Events</span>
                    <span className="text-gray-300">{selectedItinerary.events?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Duration</span>
                    <span className="text-gray-300">
                      {(() => {
                        const start = new Date(selectedItinerary.start_date);
                        const end = new Date(selectedItinerary.end_date);
                        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                        return `${days} day${days !== 1 ? 's' : ''}`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Itinerary Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#0C0E13] border border-white/20 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#0C0E13] p-5 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-lg font-semibold">New Itinerary</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Title *</label>
                  <input
                    type="text"
                    value={newItinerary.title}
                    onChange={(e) => setNewItinerary({ ...newItinerary, title: e.target.value })}
                    placeholder="Austin Trip"
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50"
                    data-testid="itinerary-title-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Type</label>
                  <div className="grid grid-cols-4 gap-2">
                    {ITINERARY_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setNewItinerary({ ...newItinerary, itinerary_type: type.value })}
                          className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-all ${
                            newItinerary.itinerary_type === type.value
                              ? 'border-white bg-white/10'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <Icon size={18} className={newItinerary.itinerary_type === type.value ? 'text-white' : 'text-gray-400'} />
                          <span className="text-xs">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Start Date *</label>
                    <input
                      type="date"
                      value={newItinerary.start_date}
                      onChange={(e) => setNewItinerary({ ...newItinerary, start_date: e.target.value })}
                      className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-white/50"
                      data-testid="itinerary-start-date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">End Date *</label>
                    <input
                      type="date"
                      value={newItinerary.end_date}
                      onChange={(e) => setNewItinerary({ ...newItinerary, end_date: e.target.value })}
                      className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-white/50"
                      data-testid="itinerary-end-date"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Location</label>
                  <input
                    type="text"
                    value={newItinerary.location}
                    onChange={(e) => setNewItinerary({ ...newItinerary, location: e.target.value })}
                    placeholder="Austin, TX"
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Description</label>
                  <textarea
                    value={newItinerary.description}
                    onChange={(e) => setNewItinerary({ ...newItinerary, description: e.target.value })}
                    placeholder="Trip details..."
                    rows={3}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50 resize-none"
                  />
                </div>
              </div>
              
              <div className="sticky bottom-0 bg-[#0C0E13] p-5 border-t border-white/10 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateItinerary}
                  className="px-5 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  data-testid="create-itinerary-submit"
                >
                  Create Itinerary
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Event Modal */}
        {showEventModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#0C0E13] border border-white/20 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#0C0E13] p-5 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-lg font-semibold">{editingEvent ? 'Edit Event' : 'Add Event'}</h2>
                <button onClick={() => { setShowEventModal(false); setEditingEvent(null); }} className="text-gray-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Event Title *</label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    placeholder="Flight to Austin"
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50"
                    data-testid="event-title-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Event Type</label>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(EVENT_TYPES).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setNewEvent({ ...newEvent, event_type: key, custom_type: key === 'other' ? newEvent.custom_type : '' })}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${
                            newEvent.event_type === key
                              ? `border-white ${config.bg}`
                              : 'border-white/10 hover:border-white/30'
                          }`}
                          title={config.label}
                        >
                          <Icon size={16} className={newEvent.event_type === key ? config.color : 'text-gray-400'} />
                          <span className="text-[10px]">{config.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Custom type input when "Other" is selected */}
                  {newEvent.event_type === 'other' && (
                    <div className="mt-3">
                      <input
                        type="text"
                        value={newEvent.custom_type}
                        onChange={(e) => setNewEvent({ ...newEvent, custom_type: e.target.value })}
                        placeholder="Enter custom event type (e.g., Spa, Tour, Workshop)"
                        className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50"
                        data-testid="event-custom-type-input"
                      />
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Date *</label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-white/50"
                    data-testid="event-date-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Start Time</label>
                    <input
                      type="time"
                      value={newEvent.start_time}
                      onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                      className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-white/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">End Time</label>
                    <input
                      type="time"
                      value={newEvent.end_time}
                      onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                      className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-white/50"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Location</label>
                  <input
                    type="text"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    placeholder="Austin-Bergstrom International Airport"
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">
                    Cost
                    <span className="ml-2 text-[10px] text-gray-500">
                      (adds to {EVENT_TYPE_TO_BUDGET_CATEGORY[newEvent.event_type] || 'Miscellaneous'})
                    </span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newEvent.cost}
                      onChange={(e) => setNewEvent({ ...newEvent, cost: e.target.value })}
                      placeholder="0.00"
                      className="w-full bg-black border border-white/20 rounded-lg pl-7 pr-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50"
                      data-testid="event-cost-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Event details..."
                    rows={2}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50 resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-1.5">
                    <Link2 size={12} /> Link
                  </label>
                  <input
                    type="url"
                    value={newEvent.link}
                    onChange={(e) => setNewEvent({ ...newEvent, link: e.target.value })}
                    placeholder="https://booking.com/reservation..."
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50"
                    data-testid="event-link-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Notes</label>
                  <textarea
                    value={newEvent.notes}
                    onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                    className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-white/50 resize-none"
                  />
                </div>
              </div>
              
              <div className="sticky bottom-0 bg-[#0C0E13] p-5 border-t border-white/10 flex justify-end gap-3">
                <button
                  onClick={() => { setShowEventModal(false); setEditingEvent(null); }}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEvent}
                  className="px-5 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  data-testid="save-event-btn"
                >
                  {editingEvent ? 'Update Event' : 'Add Event'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attachment Preview Modal */}
        {previewAttachment && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreviewAttachment(null)}>
            <div className="bg-[#0C0E13] border border-white/20 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  {previewAttachment.file_type === 'image' ? <Image size={18} className="text-gray-400" /> : 
                   previewAttachment.file_type === 'pdf' ? <FileText size={18} className="text-gray-400" /> : 
                   <File size={18} className="text-gray-400" />}
                  <span className="font-medium text-sm truncate max-w-[300px]">{previewAttachment.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDownloadAttachment}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-all"
                  >
                    <Download size={14} /> Download
                  </button>
                  <button
                    onClick={() => setPreviewAttachment(null)}
                    className="text-gray-400 hover:text-white p-1"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] flex items-center justify-center">
                {loadingPreview ? (
                  <div className="py-20">
                    <Loader2 size={32} className="animate-spin text-gray-500" />
                  </div>
                ) : previewAttachment.file_type === 'image' ? (
                  <img 
                    src={previewAttachment.data_url} 
                    alt={previewAttachment.name}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                  />
                ) : previewAttachment.file_type === 'pdf' ? (
                  <iframe
                    src={previewAttachment.data_url}
                    title={previewAttachment.name}
                    className="w-full h-[70vh] rounded-lg bg-white"
                  />
                ) : previewAttachment.file_type === 'video' ? (
                  <video 
                    src={previewAttachment.data_url} 
                    controls 
                    className="max-w-full max-h-[70vh] rounded-lg"
                  />
                ) : previewAttachment.file_type === 'audio' ? (
                  <div className="py-10">
                    <audio src={previewAttachment.data_url} controls className="w-full max-w-md" />
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <File size={48} className="mx-auto text-gray-500 mb-4" />
                    <p className="text-gray-400 mb-4">Preview not available for this file type</p>
                    <button
                      onClick={handleDownloadAttachment}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      <Download size={16} /> Download File
                    </button>
                  </div>
                 )}
              </div>
            </div>
          </div>
        )}

        {/* Event Image Preview Modal */}
        {eventImagePreview && (
          <div 
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
            onClick={() => setEventImagePreview(null)}
            data-testid="event-image-modal"
          >
            <div className="bg-[#0C0E13] border border-white/20 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <Image size={18} className="text-gray-400" />
                  <span className="font-medium text-sm truncate max-w-[300px]">{eventImagePreview.filename}</span>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={eventImagePreview.data_url}
                    download={eventImagePreview.filename}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-all"
                  >
                    <Download size={14} /> Download
                  </a>
                  <button
                    onClick={() => setEventImagePreview(null)}
                    className="text-gray-400 hover:text-white p-1"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              {/* Image Content */}
              <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] flex items-center justify-center">
                {loadingEventImage ? (
                  <div className="py-20">
                    <Loader2 size={32} className="animate-spin text-gray-500" />
                  </div>
                ) : (
                  <img 
                    src={eventImagePreview.data_url} 
                    alt={eventImagePreview.filename}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Itineraries;
